import React, { useState } from 'react';
import {
  ForgeButton,
  ForgeCircularProgress,
  ForgeDialog,
  ForgeIcon,
  ForgeIconButton,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar,
} from '@tylertech/forge-react';
import { I18n } from 'common/i18n';
import { useDerivedReport } from '../DerivedReportContext';
import { launchDeleteAssetModal } from 'common/components/AssetActionBar/components/delete_asset_modal';
import { useSaveAsNewReport } from './useSaveAsNewReport';
import { useEditDerivedReportMetadata } from '../EditDerivedReportMetadata/useEditDerivedReportMetadata';

import './StorySaveAsDialog.scss';

export function StorySaveAsDialog({
  dialogOpen,
  onCloseDialog,
  isEditing = false,
}: {
  dialogOpen: boolean;
  onCloseDialog: () => void;
  isEditing?: boolean;
}) {
  const saveAsNewReport = useSaveAsNewReport();
  const updateDerivedReport = useEditDerivedReportMetadata();

  // Choose which hook's values to use based on mode
  const { doSave, saveInProgress } = isEditing ? updateDerivedReport : saveAsNewReport;

  const { currentStory, canDelete, defaultStory } = useDerivedReport();

  const [reportName, setReportName] = useState<string | undefined>(isEditing ? currentStory.name : undefined);
  const [reportDescription, setReportDescription] = useState(isEditing ? currentStory.description || '' : '');

  const saveButtonDisabled = !reportName || saveInProgress;

  const handleSave = async () => {
    if (!reportName) {
      console.error('Save triggered with empty name, which is not valid.');
      return;
    }
    await doSave(reportName, reportDescription);
  };

  const handleDelete = () => {
    onCloseDialog();
    launchDeleteAssetModal(
      currentStory,
      // redirect will default to profile if nothing is passed
      defaultStory ? () => `/d/${defaultStory?.id}` : undefined,
    );
  };

  return (
    <ForgeDialog
      open={dialogOpen}
      on-forge-dialog-close={onCloseDialog}
      id="story-save-as-dialog"
      className={isEditing ? 'edit-mode' : ''}
      data-testid="story-save-as-dialog"
      persistent={true}
    >
      <ForgeScaffold>
        <ForgeToolbar no-divider slot="header">
          <h4 slot="start" className="forge-typography--subheading4 forge-header-title">
            {isEditing ? I18n.t('derived_story.edit_report_view') : I18n.t('derived_story.save_title')}
          </h4>
          <ForgeIconButton
            slot="end"
            data-testid="save-as-modal-x"
            onClick={onCloseDialog}
            aria-label={I18n.t('derived_story.explain')}
          >
            <ForgeIcon name="close" />
          </ForgeIconButton>
        </ForgeToolbar>

        <div className="save-as-dialog-body" slot="body">
          {!isEditing && <p className="forge-typography--body1">{I18n.t('derived_story.explain')}</p>}
          <ForgeTextField labelPosition="block-start" required={true}>
            <input
              type="text"
              data-testid="report-save-as-name-field"
              id="report-save-as-name-field"
              placeholder={I18n.t('derived_story.name_placeholder')}
              value={reportName || ''}
              onChange={(e) => setReportName(e.target.value)}
            />
            <label htmlFor="report-save-as-name-field">{I18n.t('derived_story.name_input')}</label>
          </ForgeTextField>
          <ForgeTextField labelPosition="block-start">
            <textarea
              data-testid="report-save-as-desc-field"
              id="report-save-as-desc-field"
              placeholder={I18n.t('derived_story.desc_placeholder')}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
            />
            <label htmlFor="report-save-as-desc-field">{I18n.t('derived_story.desc_input')}</label>
          </ForgeTextField>
        </div>

        <ForgeToolbar slot="footer">
          {isEditing && canDelete && (
            <ForgeButton
              variant="text"
              theme="error"
              slot="start"
              onClick={handleDelete}
              disabled={saveInProgress}
              data-testid="save-as-dialog-delete"
            >
              {I18n.t('derived_story.delete_view')}
            </ForgeButton>
          )}
          <ForgeButton
            variant="outlined"
            slot="end"
            onClick={onCloseDialog}
            disabled={saveInProgress}
            data-testid="save-as-dialog-cancel"
          >
            {I18n.t('derived_story.cancel')}
          </ForgeButton>

          <ForgeButton
            variant="raised"
            slot="end"
            disabled={saveButtonDisabled}
            onClick={handleSave}
            data-testid="save-derived-report-dialog-save"
          >
            <SaveDerivedReportContent saveInProgress={saveInProgress} />
          </ForgeButton>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}

function SaveDerivedReportContent({ saveInProgress }: { saveInProgress: boolean }) {
  if (!saveInProgress) {
    return I18n.t('derived_story.save_changes');
  }

  return (
    <>
      {I18n.t('derived_story.saving')}
      <ForgeCircularProgress slot="end" aria-label={I18n.t('derived_story.saving')} />
    </>
  );
}
